/**
 * BE Position is `{ id, person }`. Older FE shapes had Person fields on the root.
 * Helpers normalize both so badges/links don't scatter `?.person` everywhere.
 *
 * Vacant seats: with enablePositionBackedVacancies, BE nulls GraphQL `personId`
 * / `person` when storage has person_id = position_id. Legacy payloads may still
 * expose personId === positionId.
 *
 * Customer-facing positionCode is preferred when present (not on schema yet).
 */

import moment, { type Moment } from "moment";

export type PositionPersonLike = {
  aggregateId?: string | null;
  preferredName?: string | null;
  firstName?: string | null;
  surname?: string | null;
  avatar?: string | null;
  isVacancy?: boolean | null;
  positionCode?: string | null;
  [key: string]: unknown;
};

export type PositionLike = {
  id?: string | null;
  positionCode?: string | null;
  person?: PositionPersonLike | null;
  aggregateId?: string | null;
  preferredName?: string | null;
  firstName?: string | null;
  surname?: string | null;
  [key: string]: unknown;
};

export type PersonAttributeLike = {
  attributeType?: string | null;
  subtype?: string | null;
  value?: string | null;
};

export type PersonWithPositionLike = {
  aggregateId?: string | null;
  preferredName?: string | null;
  firstName?: string | null;
  isVacancy?: boolean | null;
  positionCode?: string | null;
  attributes?: PersonAttributeLike[] | null;
  filledPositions?: Array<PositionLike | null> | null;
};

type MembershipLike = {
  personId?: string | null;
  positionId?: string | null;
};

/**
 * BE defaults searchMemberships to ALLOCATION-only unless positionIds is set.
 * Supply seats are POSITION_SEAT, so team/person list queries must opt in when
 * the flag is on or vacant seats never appear.
 */
export const POSITION_BACKED_MEMBERSHIP_TYPES = [
  "ALLOCATION",
  "POSITION_SEAT",
] as const;

export type PositionBackedMembershipType =
  (typeof POSITION_BACKED_MEMBERSHIP_TYPES)[number];

/** membershipTypes for list searches that must include vacant seats. */
export function membershipTypesForPositionBackedSearch(
  enablePositionBackedVacancies: boolean | null | undefined
): PositionBackedMembershipType[] | undefined {
  return enablePositionBackedVacancies
    ? [...POSITION_BACKED_MEMBERSHIP_TYPES]
    : undefined;
}

export function getPositionPerson(
  position: PositionLike | null | undefined
): PositionPersonLike | null {
  if (!position) {
    return null;
  }
  if (position.person) {
    return position.person;
  }
  return position as PositionPersonLike;
}

export function getPositionId(
  position: PositionLike | null | undefined
): string | null {
  if (!position) {
    return null;
  }
  return (
    position.id ?? position.person?.aggregateId ?? position.aggregateId ?? null
  );
}

/** Customer code wins when set; otherwise TeamForm position id. */
export function getDisplayPositionId(
  position: PositionLike | null | undefined
): string | null {
  if (!position) {
    return null;
  }
  const customerCode =
    position.positionCode?.trim() ||
    position.person?.positionCode?.trim() ||
    null;
  if (customerCode) {
    return customerCode;
  }
  return getPositionId(position)?.trim() || null;
}

export function getRequisitionAttribute(
  person: PersonWithPositionLike | null | undefined,
  subtype: string
): string | null {
  const attrs = person?.attributes || [];
  const match = attrs.find(
    (a) =>
      a?.attributeType === "requisition" &&
      a?.subtype === subtype &&
      a?.value?.trim()
  );
  return match?.value?.trim() || null;
}

export type ForecastMemberPositionKind = "filled" | "open" | "requisition";

export type ForecastMemberPositionInfo = {
  /** TeamForm position id (always when known). */
  positionId: string;
  /** Label for the badge — customer code preferred. */
  displayId: string;
  kind: ForecastMemberPositionKind;
  requisitionId?: string | null;
  requisitionStatus?: string | null;
};

/**
 * Resolve the position decoration for a forecast/DR member person.
 * Open seat: isVacancy person. Requisition: vacancy + requisition attrs.
 * Filled: first filledPositions entry.
 */
export function getForecastMemberPositionInfo(
  person: PersonWithPositionLike | null | undefined
): ForecastMemberPositionInfo | null {
  if (!person) {
    return null;
  }

  if (person.isVacancy) {
    const positionId = person.aggregateId?.trim();
    if (!positionId) {
      return null;
    }
    const requisitionId =
      getRequisitionAttribute(person, "requisitionId") ||
      person.preferredName?.trim() ||
      person.firstName?.trim() ||
      null;
    const requisitionStatus = getRequisitionAttribute(
      person,
      "requisitionStatus"
    );
    const hasRequisition = Boolean(
      requisitionStatus || (requisitionId && /^REQ[-_]/i.test(requisitionId))
    );
    const displayId = person.positionCode?.trim() || positionId;
    if (hasRequisition) {
      return {
        positionId,
        displayId,
        kind: "requisition",
        requisitionId: requisitionId || displayId,
        requisitionStatus: requisitionStatus || null,
      };
    }
    return {
      positionId,
      displayId,
      kind: "open",
    };
  }

  const filled = person.filledPositions?.[0];
  const positionId = getPositionId(filled)?.trim();
  if (!positionId || !filled) {
    return null;
  }
  const displayId = getDisplayPositionId(filled) || positionId;
  return { positionId, displayId, kind: "filled" };
}

export function isVacantPositionSeat(
  membership: MembershipLike | null | undefined
): boolean {
  if (!membership?.positionId) {
    return false;
  }
  const { personId, positionId } = membership;
  return !personId || personId === positionId;
}

export function isFilledPositionSeat(
  membership: MembershipLike | null | undefined
): boolean {
  return Boolean(
    membership?.positionId &&
    membership.personId &&
    membership.personId !== membership.positionId
  );
}

/**
 * Four-way occupancy from the API (Position.occupancyStatus), derived from
 * establishedFte / filledFte at PIT. Prefer this over local VACANT/FILLED
 * derivation wherever Position is queried.
 */
export type PositionOccupancyStatus =
  | "VACANT"
  | "PARTIALLY_FILLED"
  | "FILLED"
  | "OVER_FILLED";

/** Display label for a four-way occupancy status (matches Positions registry). */
export function getPositionOccupancyStatusLabel(
  occupancyStatus: PositionOccupancyStatus | null | undefined
): string {
  switch (occupancyStatus) {
    case "VACANT":
      return "Vacant";
    case "PARTIALLY_FILLED":
      return "Partially filled";
    case "FILLED":
      return "Filled";
    case "OVER_FILLED":
      return "Over-filled";
    case null:
    case undefined:
      return "Vacant";
    default: {
      const _exhaustive: never = occupancyStatus;
      return _exhaustive;
    }
  }
}

/**
 * StatusDot / RegistryStatus variant for a four-way occupancy status.
 * Same mapping as PositionsRegistry columns (not Badge pills).
 */
export function getPositionOccupancyStatusDotVariant(
  occupancyStatus: PositionOccupancyStatus | null | undefined
): "default" | "warning" | "success" | "danger" {
  switch (occupancyStatus) {
    case "VACANT":
      return "default";
    case "PARTIALLY_FILLED":
      return "warning";
    case "FILLED":
      return "success";
    case "OVER_FILLED":
      return "danger";
    case null:
    case undefined:
      return "default";
    default: {
      const _exhaustive: never = occupancyStatus;
      return _exhaustive;
    }
  }
}

/** Fallback when workspace `vacantRoleLabel` is empty. */
export const DEFAULT_POSITION_LABEL = "Position";

/** Label for a position/vacancy person — prefers workspace vacantRoleLabel. */
export function getPositionEntityLabel(
  vacantRoleLabel?: string | null
): string {
  const trimmed = vacantRoleLabel?.trim();
  return trimmed || DEFAULT_POSITION_LABEL;
}

/** Badge / aria text: "Position", "Position: Name", or with id suffix. */
export function formatPositionBadgeLabel(
  vacantRoleLabel?: string | null,
  positionName?: string | null,
  positionId?: string | null
): string {
  const label = getPositionEntityLabel(vacantRoleLabel);
  const name = positionName?.trim() || null;
  const id = positionId?.trim() || null;
  if (name && id) {
    return `${label}: ${name} (${id})`;
  }
  if (name) {
    return `${label}: ${name}`;
  }
  if (id) {
    return `${label}: ${id}`;
  }
  return label;
}

type DateLike = string | Date | Moment | null | undefined;

type DatedSpan = {
  startDate?: DateLike;
  endDate?: DateLike;
};

export type PositionSeatDateRange = {
  startDate: Moment;
  /** Null = ongoing (no end). */
  endDate: Moment | null;
};

/**
 * Position lifetime is not a field on Position — it's the union of vacant-seat
 * and fill membership windows tagged with the position id.
 */
export function getPositionSeatDateRange(
  memberships: DatedSpan[] | null | undefined
): PositionSeatDateRange | null {
  if (!memberships?.length) {
    return null;
  }

  let start: Moment | null = null;
  let end: Moment | null = null;
  let hasOpenEnd = false;

  for (const membership of memberships) {
    if (!membership.startDate) {
      continue;
    }
    const spanStart = moment(membership.startDate);
    start = start ? moment.min(start, spanStart) : spanStart;

    if (!membership.endDate) {
      hasOpenEnd = true;
      continue;
    }
    const spanEnd = moment(membership.endDate);
    end = end ? moment.max(end, spanEnd) : spanEnd;
  }

  if (!start) {
    return null;
  }

  return { startDate: start, endDate: hasOpenEnd ? null : end };
}

export function formatPositionSeatDateRange(
  range: PositionSeatDateRange | null | undefined,
  dateFormat: string
): string | null {
  if (!range) {
    return null;
  }
  const start = range.startDate.format(dateFormat);
  const end = range.endDate ? range.endDate.format(dateFormat) : "Ongoing";
  return `${start} – ${end}`;
}
